import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CarFront, ShieldAlert, GraduationCap, RefreshCw, FileText, ChevronRight, Loader2, CircleCheck, CircleX } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/* Bornwood DMV — the licensing counter on your phone. Licence card with demerit
   points and interlock conditions, the written road-rules test (10 random questions,
   80% to pass), renewals, and the monthly points-scrubbing defensive driving course. */

const DEMO_STATUS = {
  licence: { class: "C", points: 4, suspend_at: 12, suspended: false, interlock: false, expired: false,
             issued_at: "2026-03-11T09:00:00", expires_at: "2028-03-11T09:00:00", status: "valid" },
  can_course: true, fees: { test: 50, licence: 250, renew: 120, course: 150 },
};
const DEMO_QUIZ = [
  { i: 0, q: "A BPD cruiser lights you up behind you. You…", o: ["Accelerate to the next exit", "Indicate, pull over safely, engine off, hands visible", "Stop dead in the middle of the lane", "Call your lawyer first, then stop"], a: 1 },
  { i: 1, q: "An EMS unit approaches with sirens on. You must…", o: ["Pace it so traffic parts for you too", "Pull to the right and stop until it passes", "Stop exactly where you are", "Change lanes left only"], a: 1 },
  { i: 2, q: "Your licence is suspended. Legally you may…", o: ["Drive to work only", "Not drive any motor vehicle, period", "Drive under 25 mph", "Drive with a licensed passenger"], a: 1 },
];

const STATUS_STYLE = {
  valid: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10",
  suspended: "text-red-300 border-red-400/40 bg-red-400/10",
  expired: "text-amber-300 border-amber-400/40 bg-amber-400/10",
};

export default function DmvApp({ demo, serverId, token, accent, sfx }) {
  const [st, setSt] = useState(demo ? DEMO_STATUS : null);
  const [paper, setPaper] = useState(null); // {questions, answers[]}
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/dmv/status`, { params: { token } })
      .then((r) => setSt(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [demo, serverId, token]);
  useEffect(() => { load(); }, [load]);

  const startTest = async () => {
    sfx?.appOpen();
    if (demo) { setPaper({ questions: DEMO_QUIZ }); setStep(0); setPicks({}); setResult(null); return; }
    try {
      const r = await api.get(`/nc/servers/${serverId}/dmv/test`, { params: { token } });
      setPaper(r.data); setStep(0); setPicks({}); setResult(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const pick = (qi, oi) => {
    sfx?.keyTick();
    const next = { ...picks, [qi]: oi };
    setPicks(next);
    setTimeout(() => setStep((s) => s + 1), 250);
  };

  const submit = async (finalPicks) => {
    if (busy) return;
    setBusy(true);
    if (demo) {
      const qs = paper.questions;
      const score = qs.filter((q) => finalPicks[q.i] === q.a).length;
      const passed = score >= Math.ceil(qs.length * 0.8);
      setResult({ passed, score, of: qs.length, message: passed ? "Passed — class C licence issued (demo)" : "Not enough — study and resit (demo)" });
      setPaper(null); setBusy(false);
      passed ? sfx?.success() : sfx?.error();
      return;
    }
    try {
      const answers = paper.questions.map((q) => finalPicks[q.i] ?? -1);
      const r = await api.post(`/nc/servers/${serverId}/dmv/test`, { answers }, { params: { token } });
      setResult(r.data); setPaper(null);
      r.data.passed ? sfx?.success() : sfx?.error();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  useEffect(() => { // auto-submit after the last question
    if (paper && step >= paper.questions.length) submit(picks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, paper]);

  const act = async (kind) => {
    if (busy) return;
    setBusy(true);
    if (demo) { toast.success(`${kind === "renew" ? "Licence renewed" : "Course complete — 3 points scrubbed"} (demo)`); setBusy(false); return; }
    try {
      const r = await api.post(`/nc/servers/${serverId}/dmv/${kind}`, {}, { params: { token } });
      toast.success(r.data.message); sfx?.success(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  /* ---- test paper ---- */
  if (paper) {
    const q = paper.questions[Math.min(step, paper.questions.length - 1)];
    return (
      <div data-testid="dmv-test" className="p-4 flex flex-col gap-3 min-h-full">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-sky-300" />
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">Written test</p>
          <p data-testid="dmv-progress" className="ml-auto font-mono text-[9px] text-white/40">{Math.min(step + 1, paper.questions.length)} / {paper.questions.length}</p>
        </div>
        <div className="flex gap-1">{paper.questions.map((_, i) => (
          <span key={i} className="h-1 flex-1 rounded-full" style={{ backgroundColor: i < step ? "#60a5fa" : "rgba(255,255,255,0.1)" }} />
        ))}</div>
        <p data-testid="dmv-question" className="font-sans font-semibold text-sm text-white leading-relaxed mt-2">{q.q}</p>
        <div className="flex flex-col gap-2 mt-1">
          {q.o.map((opt, oi) => (
            <button key={oi} data-testid={`dmv-opt-${oi}`} onClick={() => pick(q.i, oi)}
              className={`rounded-xl border px-3.5 py-3 text-left font-mono text-[10px] transition-colors flex items-center gap-2 ${picks[q.i] === oi ? "border-sky-400/60 bg-sky-400/15 text-white" : "border-white/10 bg-white/5 text-white/80 hover:bg-white/10"}`}>
              <ChevronRight className="w-3 h-3 text-sky-300 shrink-0" />{opt}
            </button>
          ))}
        </div>
        <p className="mt-auto font-mono text-[7px] text-white/30 uppercase tracking-[0.2em] text-center">pass mark 80% · ${st?.fees?.test ?? 50} sitting fee</p>
      </div>
    );
  }

  /* ---- result ---- */
  if (result) {
    return (
      <div data-testid="dmv-result" className="p-6 flex flex-col items-center justify-center gap-3 min-h-full text-center">
        {result.passed
          ? <CircleCheck className="w-12 h-12 text-emerald-300" />
          : <CircleX className="w-12 h-12 text-red-400" />}
        <p className="font-sans font-bold text-xl text-white">{result.passed ? "Passed" : "Failed"}</p>
        <p data-testid="dmv-score" className="font-mono text-3xl font-bold" style={{ color: result.passed ? "#4ade80" : "#ef4444" }}>{result.score}/{result.of}</p>
        <p className="font-mono text-[10px] text-white/60 leading-relaxed">{result.message}</p>
        <button data-testid="dmv-result-done" onClick={() => { sfx?.back(); setResult(null); }}
          className="mt-2 px-8 py-3 rounded-full font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-black" style={{ backgroundColor: accent }}>Done</button>
      </div>
    );
  }

  /* ---- status ---- */
  const lic = st?.licence;
  return (
    <div data-testid="dmv-app" className="p-3 flex flex-col gap-2.5">
      <div className="flex items-center gap-2 px-1 pb-1">
        <CarFront className="w-4 h-4 text-sky-300" />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">Bornwood DMV</p>
        <p className="ml-auto font-mono text-[7px] text-white/40 uppercase tracking-[0.1em]">licensing</p>
      </div>

      {lic ? (
        <div data-testid="dmv-licence" className="rounded-2xl border border-white/15 bg-gradient-to-br from-sky-950/60 to-zinc-900/80 p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-mono text-[7px] uppercase tracking-[0.3em] text-sky-300/80">Bornwood · driver licence</p>
              <p className="font-sans font-bold text-lg text-white mt-1">Class {lic.class}</p>
            </div>
            <span data-testid="dmv-status-chip" className={`font-mono text-[8px] font-bold uppercase tracking-[0.15em] rounded-full px-2.5 py-1 border ${STATUS_STYLE[lic.status]}`}>{lic.status}</span>
          </div>
          <div className="mt-3 flex items-center gap-4">
            <div>
              <p className="font-mono text-[7px] uppercase tracking-[0.2em] text-white/40">Demerit points</p>
              <p data-testid="dmv-points" className={`font-mono text-xl font-bold ${lic.points >= lic.suspend_at - 3 ? "text-red-300" : "text-white"}`}>{lic.points}<span className="text-white/40 text-xs">/{lic.suspend_at}</span></p>
            </div>
            <div>
              <p className="font-mono text-[7px] uppercase tracking-[0.2em] text-white/40">Expires</p>
              <p className="font-mono text-[11px] text-white/80">{String(lic.expires_at || "—").slice(0, 10)}</p>
            </div>
          </div>
          {lic.interlock && (
            <p data-testid="dmv-interlock" className="mt-2.5 font-mono text-[8px] text-amber-200 flex items-center gap-1.5 border border-amber-400/40 bg-amber-400/10 rounded-lg px-2.5 py-1.5">
              <ShieldAlert className="w-3.5 h-3.5 shrink-0" /> INTERLOCK CONDITION — breath device required in any vehicle you drive
            </p>
          )}
        </div>
      ) : (
        <div data-testid="dmv-no-licence" className="rounded-2xl border border-white/10 bg-white/5 p-4 text-center">
          <CarFront className="w-8 h-8 text-white/20 mx-auto" />
          <p className="font-mono text-[10px] text-white/60 mt-2">No licence on file.<br />Sit the written test to get on the road.</p>
        </div>
      )}

      {st && (
        <div className="flex flex-col gap-2">
          {(!lic || lic.expired) && !lic?.suspended && (
            <button data-testid="dmv-start-test" onClick={startTest}
              className="rounded-2xl bg-sky-400/15 border border-sky-400/30 p-3.5 flex items-center gap-3 hover:bg-sky-400/25 transition-colors text-left">
              <FileText className="w-4 h-4 text-sky-300 shrink-0" />
              <div className="flex-1"><p className="font-sans font-semibold text-sm text-white">Sit the written test</p>
                <p className="font-mono text-[8px] text-white/40">10 questions · 80% to pass · ${st.fees.test} sitting + ${st.fees.licence} on pass</p></div>
              <ChevronRight className="w-4 h-4 text-white/30" />
            </button>
          )}
          {lic?.expired && !lic.suspended && (
            <button data-testid="dmv-renew" onClick={() => act("renew")} disabled={busy}
              className="rounded-2xl bg-white/5 border border-white/10 p-3.5 flex items-center gap-3 hover:bg-white/10 transition-colors text-left disabled:opacity-40">
              <RefreshCw className="w-4 h-4 text-amber-300 shrink-0" />
              <div className="flex-1"><p className="font-sans font-semibold text-sm text-white">Renew licence</p>
                <p className="font-mono text-[8px] text-white/40">${st.fees.renew} · extends 2 years</p></div>
            </button>
          )}
          {lic && lic.points > 0 && !lic.suspended && st.can_course && (
            <button data-testid="dmv-course" onClick={() => act("points-course")} disabled={busy}
              className="rounded-2xl bg-white/5 border border-white/10 p-3.5 flex items-center gap-3 hover:bg-white/10 transition-colors text-left disabled:opacity-40">
              <GraduationCap className="w-4 h-4 text-emerald-300 shrink-0" />
              <div className="flex-1"><p className="font-sans font-semibold text-sm text-white">Defensive driving course</p>
                <p className="font-mono text-[8px] text-white/40">${st.fees.course} · scrubs 3 points · monthly</p></div>
            </button>
          )}
          {lic?.suspended && (
            <p data-testid="dmv-suspended-note" className="rounded-2xl border border-red-400/40 bg-red-400/10 p-3.5 font-mono text-[9px] text-red-200 leading-relaxed">
              Licence suspended at {lic.points} points. Reinstatement is a court matter — the bench clears points and lifts the suspension.
            </p>
          )}
        </div>
      )}
      {busy && <Loader2 className="w-4 h-4 animate-spin text-white/40 mx-auto" />}
      <p className="font-mono text-[7px] text-white/30 text-center uppercase tracking-[0.2em] mt-auto">results &amp; notices arrive by city mail</p>
    </div>
  );
}
